import React from 'react'
import { useCssHandles } from '../hooks/useCssHandles'
import { Layout } from '../shared/components'

const CSS_HANDLES = [
  'about',
  'hero',
  'heroContent',
  'heroTitle',
  'heroSubtitle',
  'heroImage',
  'features',
  'featuresGrid',
  'featureCard',
  'featureIcon',
  'featureTitle',
  'featureDescription',
  'story',
  'storyContent',
  'storyTitle',
  'storyText',
  'storyImage',
  'values',
  'valuesGrid',
  'valueCard',
  'valueIcon',
  'valueTitle',
  'valueDescription',
  'team',
  'teamHeader',
  'teamTitle',
  'teamGrid',
  'memberCard',
  'memberImage',
  'memberName',
  'memberRole',
  'testimonials',
  'testimonialsHeader',
  'testimonialsTitle',
  'testimonialsGrid',
  'testimonialCard',
  'testimonialContent',
  'testimonialAuthor',
  'testimonialRating',
  'contact',
  'contactContent',
  'contactTitle',
  'contactInfo',
  'contactItem',
  'contactIcon',
  'contactText'
] as const

const About: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)

  return (
    <Layout>
      <div className={`${handles.about}`}>
        {/* Hero Section */}
        <section className={`${handles.hero} relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 text-white overflow-hidden`}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className={`${handles.heroContent}`}>
                <div className="mb-6">
                  <span className="inline-block bg-yellow-500 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                    🏢 Sobre a Baterpower Baterias
                  </span>
                </div>

                <h1 className={`${handles.heroTitle} text-5xl lg:text-6xl font-bold mb-6 leading-tight`}>
                  Especialista em <span className="text-yellow-400">Baterias</span> que gera energia
                </h1>

                <p className={`${handles.heroSubtitle} text-xl mb-8 text-blue-100 leading-relaxed`}>
                  Somos uma empresa familiar, a mais de 30 anos atuando no mercado de baterias,
                  buscando a cada ano inovação e conhecimento com o objetivo de prestar sempre
                  um bom trabalho e um atendimento com excelência aos nossos clientes.
                </p>
              </div>

              <div className={`${handles.heroImage} flex`}>
                <img
                  src="/assets/logo-branco-com-slogan.svg"
                  alt="Baterpower Baterias - Sobre nós"
                  className="h-[200px] object-fit transform rotate-0 hover:rotate-3 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className={`${handles.features} py-20 bg-gray-50`}>
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Por que escolher a <span className="text-blue-600">Baterpower Baterias</span>?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Somos especialistas em baterias automotivas com mais de 15 anos de experiência no mercado
              </p>
            </div>

            <div className={`${handles.featuresGrid} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8`}>
              <div className={`${handles.featureCard} bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center`}>
                <div className={`${handles.featureIcon} w-20 h-20 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className={`${handles.featureTitle} text-xl font-bold mb-4 text-gray-900`}>
                  Qualidade
                </h3>
                <p className={`${handles.featureDescription} text-gray-600 leading-relaxed`}>
                  Trabalhamos com as melhores marcas do mercado e produtos que atendem aos mais altos padrões de qualidade.
                </p>
              </div>

              <div className={`${handles.featureCard} bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center`}>
                <div className={`${handles.featureIcon} w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className={`${handles.featureTitle} text-xl font-bold mb-4 text-gray-900`}>
                  Entrega Rápida e Dedicada
                </h3>
                <p className={`${handles.featureDescription} text-gray-600 leading-relaxed`}>
                  Temos um serviço de entrega que auxilia na sua necessidade, para um atendimento Rápido.
                  Entrega em até 24hs (região metropolitanas). Fretes grátis para compras acima de R$ 500,00 (sob consulta de região).
                </p>
              </div>

              <div className={`${handles.featureCard} bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center`}>
                <div className={`${handles.featureIcon} w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h3 className={`${handles.featureTitle} text-xl font-bold mb-4 text-gray-900`}>
                  Melhores Preços
                </h3>
                <p className={`${handles.featureDescription} text-gray-600 leading-relaxed`}>
                  Preços acessíveis no mercado, com promoções especiais e cupons de desconto.
                </p>
              </div>

              <div className={`${handles.featureCard} bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center`}>
                <div className={`${handles.featureIcon} w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
                  </svg>
                </div>
                <h3 className={`${handles.featureTitle} text-xl font-bold mb-4 text-gray-900`}>
                  Suporte Técnico
                </h3>
                <p className={`${handles.featureDescription} text-gray-600 leading-relaxed`}>
                  Equipe especializada para te ajudar a escolher a bateria ideal para seu veículo.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className={`${handles.story} py-20 bg-white`}>
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className={`${handles.storyContent}`}>
                <h2 className={`${handles.storyTitle} text-4xl font-bold text-gray-900 mb-6`}>
                  Nossa <span className="text-blue-600">História</span>
                </h2>
                <div className={`${handles.storyText} space-y-6 text-gray-600 leading-relaxed`}>
                  <p>
                    Somos uma empresa familiar, a mais de 30 anos atuando no mercado de baterias,
                    buscando a cada ano inovação e conhecimento com o objetivo de prestar sempre
                    um bom trabalho e um atendimento com excelência aos nossos clientes.
                  </p>
                  <p>
                    O nosso objetivo não é simplesmente vender, mas poder prestar um bom trabalho
                    para atender a expectativa de nossos clientes, proporcionando um bom relacionamento
                    e satisfação.
                  </p>
                  <p>
                    Com o objetivo de inovar e crescer iniciamos o projeto da baterpower, um
                    ecommerce que busca atender todo o seguimento de energia ligado a baterias e
                    pilhas.
                  </p>
                  <p>
                    Nosso foco é atender todo o território Brasileiro, facilitando a busca por
                    esses produtos e mantendo a energia em movimento.
                  </p>
                </div>
              </div>

              <div className={`${handles.storyImage} relative`}>
                <img
                  src="/assets/logo-cor-com-slogan.svg"
                  alt="História da Baterpower Baterias"
                  className="h-[200px] object-fit transform rotate-0 hover:rotate-3 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className={`${handles.values} py-20 bg-gray-50`}>
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Nossos <span className="text-blue-600">Valores</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Os princípios que guiam nosso trabalho e relacionamento com clientes
              </p>
            </div>

            <div className={`${handles.valuesGrid} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8`}>
              <div className={`${handles.valueCard} bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center`}>
                <div className={`${handles.valueIcon} w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className={`${handles.valueTitle} text-xl font-bold mb-4 text-gray-900`}>
                  Qualidade
                </h3>
                <p className={`${handles.valueDescription} text-gray-600 leading-relaxed`}>
                  Trabalhamos com as melhores marcas do mercado e produtos que atendem aos mais altos padrões de qualidade.
                </p>
              </div>

              <div className={`${handles.valueCard} bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center`}>
                <div className={`${handles.valueIcon} w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className={`${handles.valueTitle} text-xl font-bold mb-4 text-gray-900`}>
                  Confiança
                </h3>
                <p className={`${handles.valueDescription} text-gray-600 leading-relaxed`}>
                  Construímos relacionamentos duradouros baseados na transparência e confiabilidade.
                </p>
              </div>

              <div className={`${handles.valueCard} bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center`}>
                <div className={`${handles.valueIcon} w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className={`${handles.valueTitle} text-xl font-bold mb-4 text-gray-900`}>
                  Inovação
                </h3>
                <p className={`${handles.valueDescription} text-gray-600 leading-relaxed`}>
                  Estamos sempre em busca de novas tecnologias e soluções para melhor atender nossos clientes.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className={`${handles.testimonials} py-20 bg-white`}>
          <div className="max-w-7xl mx-auto px-4">
            <div className={`${handles.testimonialsHeader} text-center mb-16`}>
              <h2 className={`${handles.testimonialsTitle} text-4xl font-bold text-gray-900 mb-4`}>
                O que nossos <span className="text-blue-600">clientes</span> dizem
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Mais de 10.000 clientes satisfeitos confiam na Baterpower Baterias
              </p>
            </div>

            <div className={`${handles.testimonialsGrid} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8`}>
              <div className={`${handles.testimonialCard} bg-gray-50 p-8 rounded-2xl`}>
                <div className={`${handles.testimonialRating} flex mb-4`}>
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className={`${handles.testimonialContent} text-gray-700 mb-6 leading-relaxed`}>
                  "Excelente atendimento e produto de qualidade. A bateria chegou no prazo e está funcionando perfeitamente no meu carro há mais de 1 ano."
                </p>
                <div className={`${handles.testimonialAuthor} flex items-center`}>
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    M
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Maria Silva</div>
                    <div className="text-sm text-gray-600">Cliente desde 2022</div>
                  </div>
                </div>
              </div>

              <div className={`${handles.testimonialCard} bg-gray-50 p-8 rounded-2xl`}>
                <div className={`${handles.testimonialRating} flex mb-4`}>
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className={`${handles.testimonialContent} text-gray-700 mb-6 leading-relaxed`}>
                  "Preço justo e entrega super rápida. Recomendo para quem precisa de bateria com urgência. O suporte técnico também é muito bom."
                </p>
                <div className={`${handles.testimonialAuthor} flex items-center`}>
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    J
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">João Santos</div>
                    <div className="text-sm text-gray-600">Cliente desde 2021</div>
                  </div>
                </div>
              </div>

              <div className={`${handles.testimonialCard} bg-gray-50 p-8 rounded-2xl`}>
                <div className={`${handles.testimonialRating} flex mb-4`}>
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className={`${handles.testimonialContent} text-gray-700 mb-6 leading-relaxed`}>
                  "Site fácil de usar e com muitas opções. Encontrei exatamente a bateria que precisava para minha moto. Vou comprar novamente."
                </p>
                <div className={`${handles.testimonialAuthor} flex items-center`}>
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    A
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Ana Costa</div>
                    <div className="text-sm text-gray-600">Cliente desde 2023</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className={`${handles.contact} py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden`}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            {/* Header Section */}
            <div className="text-center mb-20">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full mb-8 shadow-2xl">
                <svg className="w-10 h-10 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h2 className={`${handles.contactTitle} text-5xl font-bold mb-6 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent`}>
                Entre em <span className="text-yellow-400">Contato</span>
              </h2>
              <p className="text-xl text-blue-200 max-w-3xl mx-auto leading-relaxed">
                Estamos aqui para ajudar você a encontrar a bateria ideal para seu veículo.
                Nossa equipe especializada está pronta para atender você com excelência.
              </p>
            </div>

            {/* Contact Cards */}
            <div className={`${handles.contactInfo} grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20`}>
              {/* Telefone */}
              <div className={`${handles.contactItem} group relative`}>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 h-full transition-all duration-300 hover:bg-white/10 hover:border-yellow-400/30 hover:scale-105 hover:shadow-2xl">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-yellow-400/25 transition-all duration-300">
                      <svg className="w-7 h-7 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-white">Telefone</h3>
                      <p className="text-blue-200 text-sm">Ligue agora</p>
                    </div>
                  </div>
                  <p className={`${handles.contactText} text-white text-lg font-medium`}>
                    (19) 3255-2356
                  </p>
                  <div className="mt-4">
                    <a
                      href="tel:+551932552356"
                      className="inline-flex items-center text-yellow-400 hover:text-yellow-300 transition-colors duration-200 font-medium"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      Ligar agora
                    </a>
                  </div>
                </div>
              </div>

              {/* E-mail */}
              <div className={`${handles.contactItem} group relative`}>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 h-full transition-all duration-300 hover:bg-white/10 hover:border-yellow-400/30 hover:scale-105 hover:shadow-2xl">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-yellow-400/25 transition-all duration-300">
                      <svg className="w-7 h-7 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-white">E-mail</h3>
                      <p className="text-blue-200 text-sm">Envie sua mensagem</p>
                    </div>
                  </div>
                  <p className={`${handles.contactText} text-white text-lg font-medium break-all`}>
                    contato@baterpowerbaterias.com.br
                  </p>
                  <div className="mt-4">
                    <a
                      href="mailto:contato@baterpowerbaterias.com.br"
                      className="inline-flex items-center text-yellow-400 hover:text-yellow-300 transition-colors duration-200 font-medium"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Enviar e-mail
                    </a>
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className={`${handles.contactItem} group relative`}>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 h-full transition-all duration-300 hover:bg-white/10 hover:border-yellow-400/30 hover:scale-105 hover:shadow-2xl">
                  <div className="flex items-center mb-6">
                    <div className="w-14 h-14 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-yellow-400/25 transition-all duration-300">
                      <svg className="w-7 h-7 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-white">Endereço</h3>
                      <p className="text-blue-200 text-sm">Visite nossa loja</p>
                    </div>
                  </div>
                  <div className={`${handles.contactText} text-white text-lg font-medium space-y-1`}>
                    <p>Av. José de Sousa Campos, 2086</p>
                    <p>Nova Campinas, Campinas - SP</p>
                    <p>CEP: 13025-320</p>
                  </div>
                  <div className="mt-4">
                    <a
                      href="https://maps.google.com/?q=Av.+José+de+Sousa+Campos,+2086,+Nova+Campinas,+Campinas+-+SP"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-yellow-400 hover:text-yellow-300 transition-colors duration-200 font-medium"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Ver no mapa
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Horário de Funcionamento */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full mb-6 shadow-2xl">
                <svg className="w-8 h-8 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold mb-8 text-yellow-400">Horário de Funcionamento</h3>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 max-w-lg mx-auto shadow-2xl">
                <div className="space-y-4 text-white">
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="font-semibold text-lg">Segunda a Sexta</span>
                    <span className="text-yellow-400 font-bold text-lg">08h às 18h</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="font-semibold text-lg">Sábado</span>
                    <span className="text-yellow-400 font-bold text-lg">08h às 14h</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="font-semibold text-lg text-blue-200">Domingo</span>
                    <span className="text-red-400 font-bold text-lg">Fechado</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default About
